/**
 * Client portal "Talk to our AI assistant" button + call panel.
 *
 * Shown when Portal Configuration → Voice assistant is on and the chosen
 * voice agent is published (GET /portal/:slug/voice/config). The call itself
 * is browser ⇄ ElevenLabs (eleven-browser-call.ts); KC3 tools run through
 * /portal/:slug/voice/session/:id/tool. A hand-off ends the AI call and asks
 * the portal's own chat launcher to connect a human (event below).
 */
import { useEffect, useRef, useState, type JSX } from 'react'
import { romanizeIndic } from '@kc3/types'
import { ClientCallWidget } from '../ClientCallWidget.js'
import { Bot, Mic, MicOff, PhoneOff, X } from 'lucide-react'
import { clientApi } from '../../../api/client-portal.js'
import { useClientAuthStore } from '../../../store/clientAuthStore.js'
import { ElevenBrowserCall, type BrowserCallHandlers } from './eleven-browser-call.js'
import { OpenAiBrowserCall } from './openai-browser-call.js'
import { uploadRecording } from './call-recorder.js'

/** Dispatched on hand-off; ClientChatLauncher opens its call (or live chat) flow. */
export const PORTAL_HANDOFF_EVENT = 'kc3:portal-connect-human'

interface VoiceConfig { consentText?: string | null; available: boolean; provider?: 'elevenlabs' | 'openai'; label?: string; position?: 'bottom_right' | 'bottom_left'; color?: string; requireLogin?: boolean; agentName?: string }
type SessionResponse =
  | { provider: 'elevenlabs'; sessionId: string; token: string; signedUrl: string; dynamicVariables: Record<string, string> }
  | { provider: 'openai'; sessionId: string; token: string; answerSdp: string; greetingInstruction: string; language?: string | null }
type Phase = 'closed' | 'consent' | 'connecting' | 'live' | 'handoff' | 'ended' | 'error'
type Call = ElevenBrowserCall | OpenAiBrowserCall

/**
 * Headers for the per-call endpoints. clientApi uses custom headers INSTEAD
 * of its JSON default, so content-type must be repeated here — without it
 * the API never parsed these bodies (every transcript line was a 400, tool
 * calls arrived without a name).
 */
const voiceHeaders = (token: string): Record<string, string> => ({ 'content-type': 'application/json', 'x-voice-token': token })

/** Website widget mode: inside the voice.js iframe, bound to one widget. */
export interface VoiceEmbed { widgetId: string; token: string }

export function ClientVoiceAssistant({ tenantSlug, embed }: { tenantSlug: string; embed?: VoiceEmbed }): JSX.Element | null {
  const loggedIn = useClientAuthStore((s) => !!s.user)
  const [cfg, setCfg] = useState<VoiceConfig | null>(null)
  const [cfgError, setCfgError] = useState<string | null>(null)
  // The loader already showed the button — the iframe opens on the consent step.
  const [phase, setPhase] = useState<Phase>(embed ? 'consent' : 'closed')
  /** Set when the AI transferred the caller: the call widget dials the hand-off queue. */
  const [handoffTicket, setHandoffTicket] = useState<{ sessionId: string; token: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [captions, setCaptions] = useState<{ role: 'caller' | 'bot'; text: string }[]>([])
  const [speaking, setSpeaking] = useState(false)
  const [level, setLevel] = useState(0)
  const [muted, setMuted] = useState(false)
  const [seconds, setSeconds] = useState(0)
  const call = useRef<Call | null>(null)
  const session = useRef<SessionResponse | null>(null)
  const handoff = useRef(false)

  useEffect(() => {
    let cancelled = false
    const q = embed ? `?widget=${encodeURIComponent(embed.widgetId)}&t=${encodeURIComponent(embed.token)}` : ''
    clientApi.get<VoiceConfig>(`/portal/${tenantSlug}/voice/config${q}`)
      .then((c) => { if (!cancelled) setCfg(c) })
      .catch((e: { message?: string }) => {
        if (cancelled) return
        setCfg({ available: false })
        setCfgError(e.message ?? 'The assistant is not available right now.')
      })
    return () => { cancelled = true }
  }, [tenantSlug, embed?.widgetId, embed?.token])

  useEffect(() => {
    if (phase !== 'live') return
    const t = setInterval(() => setSeconds((s) => s + 1), 1000)
    return () => clearInterval(t)
  }, [phase])

  // Leaving the page ends the call cleanly (best effort).
  useEffect(() => {
    const onHide = (): void => { if (call.current) void finish('page_closed') }
    window.addEventListener('pagehide', onHide)
    return () => window.removeEventListener('pagehide', onHide)
  })

  const finish = async (reason: string, handedOff = false): Promise<void> => {
    const s = session.current
    const c = call.current
    call.current = null
    session.current = null
    // Stop the recorder before the audio graph is torn down.
    const recording = c ? await c.finishRecording().catch(() => null) : null
    c?.stop()
    if (s) {
      await clientApi.post(`/portal/${tenantSlug}/voice/session/${s.sessionId}/end`, { reason, handedOff }, voiceHeaders(s.token)).catch(() => undefined)
      if (recording) void uploadRecording(tenantSlug, s.sessionId, s.token, recording)
    }
  }

  const embedBody = embed ? { widgetId: embed.widgetId, embedToken: embed.token } : {}

  const start = async (): Promise<void> => {
    setPhase('connecting')
    setError(null)
    setCaptions([])
    setSeconds(0)
    handoff.current = false
    try {
      const sessionHeaders = (): Record<string, string> => voiceHeaders(session.current?.token ?? '')
      const handlers: BrowserCallHandlers = {
        onStarted: (conversationId) => {
          setPhase('live')
          const s = session.current
          if (s) void clientApi.post(`/portal/${tenantSlug}/voice/session/${s.sessionId}/started`, { conversationId }, sessionHeaders()).catch(() => undefined)
        },
        // Hindi lines in English letters, like the AI's own Hinglish.
        onCaption: (role, text) => setCaptions((xs) => [...xs.slice(-5), { role, text: romanizeIndic(text) }]),
        onSpeaking: setSpeaking,
        onLevel: setLevel,
        onToolCall: async (name, parameters) => {
          const s = session.current
          if (!s) return { ok: false, error: 'CALL_ENDED' }
          const r = await clientApi.post<{ result: unknown; action?: 'end' | 'handoff' }>(
            `/portal/${tenantSlug}/voice/session/${s.sessionId}/tool`, { name, parameters }, sessionHeaders())
          if (r.action === 'end') {
            const c = call.current
            // OpenAI: wait until the goodbye has actually been spoken; if the
            // caller says something meanwhile the call simply continues.
            if (c instanceof OpenAiBrowserCall) {
              void c.waitForGoodbye().then((ok) => { if (ok && call.current === c) { void finish('agent_ended'); setPhase('ended') } })
            } else {
              setTimeout(() => void waitThen(() => { void finish('agent_ended'); setPhase('ended') }), 300)
            }
          }
          if (r.action === 'handoff') {
            handoff.current = true
            // Real transfer: once the AI's "connecting you" line has played, end
            // the AI call and dial the agent's hand-off queue from this widget
            // (the human agent gets the AI summary on their screen).
            const ticket = { sessionId: s.sessionId, token: s.token }
            setTimeout(() => void waitThen(() => {
              void finish('handed_off', true).then(() => { setHandoffTicket(ticket); setPhase('handoff') })
            }), 300)
          }
          return r.result
        },
        onClosed: () => {
          if (session.current) void finish('provider_closed')
          setPhase((p) => (p === 'live' || p === 'connecting' ? 'ended' : p))
        },
      }

      if (cfg?.provider === 'openai') {
        // WebRTC: the offer goes to KC3, which completes it with OpenAI.
        const c = new OpenAiBrowserCall({
          ...handlers,
          onTurn: (role, text, itemId) => {
            const s = session.current
            if (s) void clientApi.post(`/portal/${tenantSlug}/voice/session/${s.sessionId}/turns`, { role, text, itemId }, sessionHeaders()).catch(() => undefined)
          },
        })
        call.current = c
        const sdp = await c.prepare()
        const s = await clientApi.post<SessionResponse>(`/portal/${tenantSlug}/voice/session`, { sdp, ...embedBody })
        session.current = s
        if (s.provider !== 'openai') throw new Error('The assistant configuration changed — please try again.')
        await c.start(s.answerSdp, s.greetingInstruction, s.language)
      } else {
        const s = await clientApi.post<SessionResponse>(`/portal/${tenantSlug}/voice/session`, { ...embedBody })
        session.current = s
        if (s.provider !== 'elevenlabs') throw new Error('The assistant configuration changed — please try again.')
        const c = new ElevenBrowserCall(handlers)
        call.current = c
        await c.start(s.signedUrl, s.dynamicVariables)
      }
    } catch (err) {
      await finish('setup_failed')
      const e = err as { status?: number; message?: string; name?: string }
      setError(
        e.name === 'NotAllowedError' ? 'Microphone access was blocked. Allow the microphone in your browser and try again.'
          : e.message ?? 'The assistant is not available right now.',
      )
      setPhase('error')
    }
  }

  /** Let the AI finish its sentence (≤ 6 s) before acting on end / hand-off. */
  const waitThen = async (fn: () => void): Promise<void> => {
    const until = Date.now() + 6000
    while (call.current && call.current.pendingMs() > 0 && Date.now() < until) await new Promise((r) => setTimeout(r, 150))
    fn()
  }

  if (embed && !cfg?.available) {
    return (
      <div className="flex h-screen items-center justify-center p-4">
        <div className="w-full rounded-2xl bg-white p-4 text-center text-sm text-slate-600 shadow ring-1 ring-slate-200">
          {cfg ? (cfgError ?? 'The assistant is not available right now.') : 'Loading…'}
          {cfg && <button type="button" onClick={() => window.parent.postMessage({ type: 'kc3-voice:close' }, '*')} className="mt-3 block w-full rounded-full border border-slate-200 py-2 text-[13px] font-medium hover:bg-slate-50">Close</button>}
        </div>
      </div>
    )
  }
  if (!cfg?.available || (cfg.requireLogin && !loggedIn && !embed)) return null
  const color = cfg.color ?? '#3366FF'
  const side = cfg.position === 'bottom_right' ? 'right-6' : 'left-6'
  const mmss = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`

  if (phase === 'handoff' && handoffTicket) {
    return (
      <ClientCallWidget
        tenantSlug={tenantSlug}
        aiHandoff={handoffTicket}
        perInteractionEnabled={false}
        ratingScale={5}
        onClose={() => { setHandoffTicket(null); setPhase('closed') }}
        onContinueChat={() => { setHandoffTicket(null); setPhase('closed'); window.dispatchEvent(new CustomEvent(PORTAL_HANDOFF_EVENT)) }}
      />
    )
  }

  if (phase === 'closed') {
    return (
      <button type="button" onClick={() => setPhase('consent')} aria-label={cfg.label}
        className={`fixed bottom-6 ${side} z-[1000] inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-white shadow-lg transition hover:brightness-110`}
        style={{ backgroundColor: color }}>
        <Mic className="h-4 w-4" /> {cfg.label}
      </button>
    )
  }

  const close = (): void => {
    void finish('visitor_closed')
    if (embed) {
      // Hide the iframe; it reopens on the consent step.
      window.parent.postMessage({ type: 'kc3-voice:close' }, '*')
      setPhase('consent')
    } else setPhase('closed')
  }

  return (
    <div className={embed
      ? 'fixed inset-0 flex flex-col overflow-hidden rounded-[18px] bg-white'
      : `fixed bottom-6 ${side} z-[1000] w-[340px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200`}>
      <header className="flex items-center gap-2 px-4 py-3 text-white" style={{ backgroundColor: color }}>
        <Bot className="h-5 w-5" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{cfg.agentName ?? 'AI assistant'}</p>
          <p className="text-[11px] opacity-90">{phase === 'live' ? `${speaking ? 'Speaking…' : 'Listening…'} · ${mmss}` : 'AI voice assistant'}</p>
        </div>
        <button type="button" aria-label="Close" onClick={close} className="rounded-full p-1 hover:bg-white/20"><X className="h-4 w-4" /></button>
      </header>

      <div className={`space-y-3 px-4 py-4 text-sm text-slate-700 ${embed ? 'flex-1 overflow-y-auto' : ''}`}>
        {phase === 'consent' && (
          <>
            <p>{cfg.consentText?.trim() || "You'll talk to our AI assistant using your microphone. The call is answered by AI and may be recorded."}</p>
            <button type="button" onClick={() => void start()} className="w-full rounded-full py-2.5 font-semibold text-white" style={{ backgroundColor: color }}>Start call</button>
          </>
        )}
        {phase === 'connecting' && <p className="py-4 text-center text-slate-500">Connecting to the assistant…</p>}
        {phase === 'live' && (
          <>
            <div className="flex items-center justify-center gap-1 py-2" aria-hidden>
              {[0.4, 0.7, 1, 0.7, 0.4].map((k, i) => (
                <span key={i} className="w-1.5 rounded-full transition-all" style={{ backgroundColor: color, height: `${8 + Math.min(1, (speaking ? 0.6 : level * 3)) * 32 * k}px`, opacity: speaking || level > 0.02 ? 1 : 0.35 }} />
              ))}
            </div>
            <div className={`${embed ? 'max-h-72' : 'max-h-40'} space-y-1.5 overflow-y-auto`} aria-live="polite">
              {captions.length === 0 && <p className="text-center text-xs text-slate-400">Say hello — the assistant is listening.</p>}
              {captions.map((c, i) => (
                <p key={i} className={`rounded-lg px-2.5 py-1.5 text-[13px] ${c.role === 'caller' ? 'ml-6 bg-slate-100' : 'mr-6 bg-blue-50'}`}>{c.text}</p>
              ))}
            </div>
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={() => { const m = !muted; setMuted(m); call.current?.setMuted(m) }}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-slate-200 py-2 text-[13px] font-medium hover:bg-slate-50">
                {muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />} {muted ? 'Unmute' : 'Mute'}
              </button>
              <button type="button" onClick={() => { void finish('visitor_ended'); setPhase('ended') }}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full bg-red-600 py-2 text-[13px] font-semibold text-white hover:bg-red-700">
                <PhoneOff className="h-4 w-4" /> End call
              </button>
            </div>
          </>
        )}
        {phase === 'handoff' && !handoffTicket && <p className="py-3 text-center">Connecting you to a member of our team…</p>}
        {phase === 'ended' && (
          <>
            <p className="py-2 text-center">Thanks for calling — the call has ended.</p>
            <button type="button" onClick={() => setPhase('consent')} className="w-full rounded-full border border-slate-200 py-2 text-[13px] font-medium hover:bg-slate-50">Call again</button>
          </>
        )}
        {phase === 'error' && (
          <>
            <p className="rounded-lg bg-red-50 px-3 py-2 text-red-700">{error}</p>
            <button type="button" onClick={() => setPhase('consent')} className="w-full rounded-full border border-slate-200 py-2 text-[13px] font-medium hover:bg-slate-50">Try again</button>
          </>
        )}
      </div>
    </div>
  )
}
